<template>
  <div>
    <div class="mb-4">
      <v-btn
        color="primary"
        :to="{ name: 'my-workings.ask-support', params: { id: id } }"
        class="mr-3"
      >
        {{ $t('Ask for support') }}
      </v-btn>

      <v-btn color="secondary" :disabled="!canEvaluation" @click="evaluation">
        {{ $t('Evaluation') }}
      </v-btn>
    </div>

    <div class="mb-4">
      <task-basic-info :task="task" />
    </div>
    <div>
      <work-process-record
        :task-id="task?.id"
        :records="task?.records ?? []"
        :show-comment="true"
        :show-delete="canEdit"
      />
    </div>
    <div class="mt-4" v-if="isFinal">
      <v-row>
        <v-col cols="12" lg="6">
          <v-card>
            <v-card-title>{{ $t('Final Task Result') }}</v-card-title>
            <v-card-text>
              <row-line :label="$t('Summary')">
                {{ task.result_summary }}
              </row-line>

              <row-line :label="$t('Files')" :line="false">
                <single-file-item
                  v-for="file in task.files"
                  :key="`file-${file.id}`"
                  :file="file"
                />
              </row-line>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6">
          <div>
            <review
              :review="task.employee_review"
              :title="$t('Employee review')"
            />
          </div>
        </v-col>
      </v-row>
    </div>
    <v-card class="mt-4" v-if="canEdit">
      <v-card-title>{{ $t('Create Work Process Record') }}</v-card-title>
      <v-card-text>
        <alert />
        <v-form ref="taskRecordForm" v-model="valid" lazy-validation>
          <row-line :label="$t('Result')">
            <v-text-field
              v-model="form.result"
              :rules="rules.result"
              hide-details="auto"
              required
              outlined
              dense
            />
          </row-line>

          <row-line :label="$t('Detail')">
            <vue-editor v-model="form.detail" />
          </row-line>

          <row-line :label="$t('Documents')">
            <v-file-input
              v-model="form.files_upload"
              accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
              counter
              show-size
              :label="$t('Select your files')"
              prepend-icon=""
              append-icon="mdi-paperclip"
              outlined
              hide-details="auto"
              dense
              multiple
              :rules="rules.document"
            >
              <template v-slot:selection="{ index, text }">
                <v-chip v-if="index < 2" label small>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </row-line>
        </v-form>
        <div class="text-center mt-4">
          <v-btn color="primary" :submitted="submitted" @click="submit">{{
            $t('Submit')
          }}</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <div class="text-center my-6">
      <v-btn
        color="secondary"
        class="mr-3"
        @click="__back('my-workings.index', $event)"
        >{{ $t('Back') }}</v-btn
      >
      <v-btn
        v-if="canEdit"
        color="primary"
        :to="{ name: 'my-workings.completing', params: { id } }"
        >{{ $t('Complete task') }}</v-btn
      >
    </div>

    <dialog-project-manager-review
      ref="projectManagerReview"
      @submit="submittedReviewed"
    />
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify'
import { utcToLocalDateTimeLess } from '@utils'
import TaskBasicInfo from '@/components/TaskBasicInfo'
import RowLine from '@/components/RowLine'
import Alert from '@/components/Alert'
import jsonToFormData from 'json-form-data'
import validation from '@utils/validation'
import WorkProcessRecord from './components/WorkProcessRecord'
import Review from './components/Review'
import SingleFileItem from '@/components/SingleFileItem'
import DialogProjectManagerReview from '../task-evaluation/components/DialogProjectManagerReview'
import { VueEditor } from 'vue2-editor'

export default {
  components: {
    WorkProcessRecord,
    TaskBasicInfo,
    RowLine,
    Alert,
    Review,
    SingleFileItem,
    DialogProjectManagerReview,
    VueEditor
  },
  data() {
    return {
      valid: true,
      submitted: false,
      form: {
        result: '',
        detail: '',
        files_upload: []
      },
      rules: {
        result: [
          (v) => validation.required(v),
          (v) => validation.maxlength(v, 191)
        ],
        document: [
          (v) => validation.accept(v),
          (v) => validation.arrayMax(v, 5),
          (v) => validation.filesize(v, 10 * 1024 * 1024)
        ]
      }
    }
  },
  computed: {
    task: get('task/task'),
    userId: get('user/id'),
    userRole: get('user/role'),

    id() {
      return this.$route.params.id
    },

    canEdit() {
      if (this.userId !== parseInt(this.task?.user_id)) {
        return false
      }
      return ['pending', 'in_progress'].includes(this.task?.status)
    },

    isFinal() {
      return ['completed', 'reviewed'].includes(this.task.status)
    },

    canEvaluation() {
      return (
        (this.userId === this.task.project.user_id &&
          ['completed', 'reviewed'].includes(this.task.status)) ||
        (this.userRole === 'admin' && ['reviewed'].includes(this.task.status))
      )
    }
  },

  created() {
    this.getTask(this.id)
  },

  methods: {
    getTask: call('task/getTask'),
    createRecord: call('task-record/create'),
    hideAlert: call('alert/hide'),
    loading: call('app/setLoading'),
    utcToLocalDateTimeLess,
    async submit(e) {
      e.preventDefault()
      const valid = this.$refs.taskRecordForm.validate()
      if (!valid) {
        alert(this.$t('Please check data before submit!'))
      } else {
        const data = {
          task_id: this.id,
          ...this.form
        }
        this.hideAlert()
        this.loading(true)
        const formData = jsonToFormData(data)
        const status = await this.createRecord(formData)
        this.loading(false)
        if (status) {
          this.$refs.taskRecordForm.reset()
          this.form.detail = null
        }
      }
      return false
    },

    evaluation() {
      const item = {
        id: this.id,
        status: this.task.status,
        manager_review: this.task.manager_review || null
      }
      this.$refs.projectManagerReview.setData(item)
    },

    submittedReviewed(task) {
      this.getTask(this.id)
    }
  }
}
</script>
